import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Russian plural helper: pick correct form based on number.
// forms[0] = one (1, 21, 31…), forms[1] = few (2-4, 22-24…), forms[2] = many (0, 5-20, 25-30…)
export function plural(n: number, forms: [string, string, string]): string {
  const abs = Math.abs(n) % 100;
  const n10 = abs % 10;
  if (abs > 10 && abs < 20) return forms[2];
  if (n10 > 1 && n10 < 5) return forms[1];
  if (n10 === 1) return forms[0];
  return forms[2];
}

export function formatDate(date: Date | string): string {
  const d = new Date(date);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'только что';
  if (minutes < 60) return `${minutes} ${plural(minutes, ['минуту', 'минуты', 'минут'])} назад`;
  if (hours < 24) return `${hours} ${plural(hours, ['час', 'часа', 'часов'])} назад`;
  if (days < 7) return `${days} ${plural(days, ['день', 'дня', 'дней'])} назад`;
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

export function formatTime(date: Date | string): string {
  return new Date(date).toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * "Last seen" label for a user profile / DM header.
 *  - If status === 'online' → "в сети"
 *  - If lastSeenAt is null   → null (caller falls back to generic "Не в сети")
 *  - Else → "был(а) 5 минут назад", "был(а) вчера", "был(а) 12 мая"
 */
export function formatLastSeen(lastSeenAt: Date | string | null | undefined, status?: string): string | null {
  if (status && status.toLowerCase() === 'online') return 'в сети';
  if (!lastSeenAt) return null;

  const d = new Date(lastSeenAt);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'был(а) только что';
  if (minutes < 60) return `был(а) ${minutes} ${plural(minutes, ['минуту', 'минуты', 'минут'])} назад`;
  if (hours < 24) return `был(а) ${hours} ${plural(hours, ['час', 'часа', 'часов'])} назад`;
  if (days === 1) return 'был(а) вчера';
  if (days < 7) return `был(а) ${days} ${plural(days, ['день', 'дня', 'дней'])} назад`;

  return `был(а) ${d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}`;
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function truncate(str: string, len: number): string {
  if (str.length <= len) return str;
  return str.slice(0, len) + '...';
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 15);
}

